/**
 * Build-version cache busting.
 *
 * At build time, Vite stamps `__BUILD_ID__` (see vite.config.ts `define`)
 * and injects the same value into index.html as `<meta name="build-id">`.
 * On the client we periodically re-fetch `/index.html` (bypassing the HTTP
 * cache) and compare the freshly-served build id against the one bundled
 * into this JS chunk. On mismatch we:
 *   1. purge caches managed by us (`workbox|precache|runtime|vite|assets|app-shell`)
 *   2. unregister non-messaging service workers
 *   3. force a hard reload so the browser picks up the new index.html and
 *      its new chunk manifest
 *
 * Reload is one-shot per session (guarded by sessionStorage) so a
 * misconfigured CDN can never trigger an infinite reload loop.
 */
import { logCacheEvent } from "./cache-cleanup-log";


declare const __BUILD_ID__: string;

const RELOAD_KEY = "build-version-reloaded";
const BUILT_IN_BUILD_ID: string =
  typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

async function readDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?_bv=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(
      /<meta\s+name=["']build-id["']\s+content=["']([^"']+)["']/i,
    );
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

async function purgeAppCaches(): Promise<void> {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      const targets = keys.filter((k) =>
        /workbox|precache|runtime|vite|assets|app-shell/i.test(k),
      );
      const results = await Promise.allSettled(targets.map((k) => caches.delete(k)));
      const deleted = targets.filter((_, i) => results[i].status === "fulfilled");
      logCacheEvent("build-version", "info", "Purged caches before reload", {
        deleted,
      });
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      const unregistered: string[] = [];
      await Promise.allSettled(
        regs.map(async (r) => {
          const url =
            r.active?.scriptURL ??
            r.installing?.scriptURL ??
            r.waiting?.scriptURL ??
            "";
          if (/messaging|onesignal|fcm/i.test(url)) return;
          const ok = await r.unregister();
          if (ok) unregistered.push(url || r.scope);
        }),
      );
      if (unregistered.length > 0) {
        logCacheEvent("build-version", "info", "Unregistered service workers before reload", {
          unregistered,
        });
      }
    }
  } catch (err) {
    logCacheEvent("build-version", "error", "Pre-reload purge failed", {
      message: err instanceof Error ? err.message : String(err),
    });
  }
}

async function checkOnce(): Promise<void> {
  const deployed = await readDeployedBuildId();
  if (!deployed) {
    logCacheEvent("build-version", "warn", "Could not read deployed build id");
    return;
  }
  if (deployed === BUILT_IN_BUILD_ID) return;

  let alreadyReloaded = false;
  try {
    alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY) === "1";
    window.sessionStorage.setItem(RELOAD_KEY, "1");
  } catch {
    /* ignore */
  }
  if (alreadyReloaded) {
    logCacheEvent("build-version", "warn", "Build id mismatch but reload already attempted", {
      bundled: BUILT_IN_BUILD_ID,
      deployed,
    });
    return;
  }

  logCacheEvent("build-version", "info", "Build id changed — reloading", {
    bundled: BUILT_IN_BUILD_ID,
    deployed,
  });
  await purgeAppCaches();
  window.location.reload();
}


export function startBuildVersionCheck(): void {
  if (typeof window === "undefined") return;
  // Don't run inside the Lovable editor preview iframe — it reloads on its own
  // and we don't want to fight the iframe lifecycle.
  if (window.top !== window.self) return;
  if (BUILT_IN_BUILD_ID === "dev") return;

  // Clear the one-shot guard once we successfully boot on a matching build.
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* ignore */
  }

  const safeCheck = () => {
    void checkOnce();
  };

  // Periodic check.
  window.setInterval(safeCheck, CHECK_INTERVAL_MS);
  // Tab regains focus / becomes visible — common moment for stale users.
  window.addEventListener("focus", safeCheck);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") safeCheck();
  });
}

export const __BUILD_ID_FOR_TEST__ = BUILT_IN_BUILD_ID;
